import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Autocomplete, createFilterOptions } from '@mui/material';
import { Stack, Typography, Avatar, Divider } from '@datanomik/commons-ui';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useAccounts } from '@/features/cash-pooling/hooks';
import { AccountResponse, AccountType } from '@/features/cash-pooling/types';
import { getAccounts } from '@/features/cash-pooling/services/get-accounts.service';
import { searchOwnerFiscalNumbers } from '@/features/cash-pooling/services/get-owners.service';
import { formatCnpj } from '@/features/bank-integrations/utils/cnpj.utils';
import { useDebounce } from '@/hooks/useDebounce';
import { AccountAutocompleteInput } from '../../rule-creation/rule-creation-form/components/CashPoolingAccountAutocomplete/AccountAutocompleteInput';

// ─── Transfer-specific option (compact, no owner/bank name) ─────────────────

const TransferOption: React.FC<React.HTMLAttributes<HTMLLIElement> & { option: AccountResponse }> = ({ option, ...props }) => {
  const { style: _style, ...restProps } = props as any;
  return (
    <>
      <li {...restProps} style={{ display: 'block', padding: '14px 16px', margin: 0, cursor: 'pointer' }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
          <Avatar
            src={option.institution.logo}
            alt={option.institution.name}
            sx={{
              width: 24,
              height: 24,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'transparent',
            }}
            imgProps={{ sx: { objectFit: 'contain' } }}
          />
          <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Typography color="text.secondary" noWrap>
              Agência: {option.account.branch} • Conta: {option.account.accountNumber}
            </Typography>
          </Stack>
        </Stack>
      </li>
      <Divider />
    </>
  );
};

// ─── Props ───────────────────────────────────────────────────────────────────

interface TransferAccountAutocompleteProps {
  value: string | null;
  onChange: (accountId: string | null) => void;
  accountType: AccountType;
  fiscalNumber?: string;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  onAccountDataChange?: (account: AccountResponse | null) => void;
  excludedAccountIds?: string[];
}

// ─── Component ───────────────────────────────────────────────────────────────

export const TransferAccountAutocomplete: React.FC<TransferAccountAutocompleteProps> = ({
  value,
  onChange,
  accountType,
  fiscalNumber,
  label,
  placeholder,
  error,
  helperText,
  onAccountDataChange,
  excludedAccountIds = [],
}) => {
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);

  // 1. Base accounts (always loaded)
  const { data: baseData, isLoading: isLoadingBase } = useAccounts({ type: accountType, fiscalNumber });
  const baseAccounts = useMemo(() => baseData?.content || [], [baseData?.content]);
  const hasMorePages = (baseData?.totalPages ?? 1) > 1;

  // 2. Search owners matching the typed text (only when base results are paginated and not filtered by fiscalNumber)
  const { data: matchedFiscalNumbers = [] } = useQuery({
    queryKey: ['cashPooling', 'ownerSearch', accountType, debouncedSearch],
    queryFn: () => searchOwnerFiscalNumbers(debouncedSearch),
    enabled: hasMorePages && !fiscalNumber && debouncedSearch.length >= 3,
    staleTime: 5 * 60 * 1000,
  });

  // 3. Fetch accounts for each matched owner
  const ownerAccountQueries = useQueries({
    queries: matchedFiscalNumbers.map((fn) => ({
      queryKey: ['cashPooling', 'accounts', { type: accountType, fiscalNumber: fn }],
      queryFn: () => getAccounts({ type: accountType, fiscalNumber: fn }),
      staleTime: 5 * 60 * 1000,
    })),
  });

  // 4. Merge base + owner-searched accounts (deduplicated)
  const accounts = useMemo(() => {
    const seenIds = new Set(baseAccounts.map((a) => a.account.accountId));
    const merged = [...baseAccounts];

    for (const query of ownerAccountQueries) {
      if (query.data?.content) {
        for (const acc of query.data.content) {
          if (!seenIds.has(acc.account.accountId)) {
            seenIds.add(acc.account.accountId);
            merged.push(acc);
          }
        }
      }
    }

    return merged;
  }, [baseAccounts, ownerAccountQueries]);

  const isLoading = isLoadingBase || ownerAccountQueries.some((q) => q.isLoading);

  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const [selectedAccountCache, setSelectedAccountCache] = useState<AccountResponse | null>(null);

  const selectedAccount = useMemo(() => {
    const found = accounts.find((acc) => acc.account.accountId === value) || null;
    if (!found && value && selectedAccountCache?.account.accountId === value) {
      return selectedAccountCache;
    }
    return found;
  }, [accounts, value, selectedAccountCache]);

  useEffect(() => {
    if (selectedAccount && selectedAccount.account.accountId === value) {
      setSelectedAccountCache(selectedAccount);
    } else if (!value) {
      setSelectedAccountCache(null);
    }
  }, [selectedAccount, value]);

  const handleChange = useCallback((_: any, newValue: AccountResponse | null) => {
    onChangeRef.current(newValue?.account.accountId || null);
    onAccountDataChange?.(newValue);
  }, [onAccountDataChange]);

  const handleInputChange = useCallback((_: any, newInputValue: string, reason: string) => {
    setInputValue(newInputValue);
    if (reason === 'input') {
      setSearchQuery(newInputValue);
    } else if (reason === 'clear') {
      setSearchQuery('');
    }
  }, []);

  const options = useMemo(() => {
    let extra: AccountResponse[] = [];
    if (selectedAccountCache && !accounts.find(acc => acc.account.accountId === selectedAccountCache.account.accountId)) {
      extra = [selectedAccountCache];
    }
    const all = [...extra, ...accounts];
    return excludedAccountIds.length > 0
      ? all.filter(acc => !excludedAccountIds.includes(acc.account.accountId))
      : all;
  }, [accounts, selectedAccountCache, excludedAccountIds]);

  const fiscalNumberToName = useMemo(() => {
    const map = new Map<string, string>();
    options.forEach(option => {
      const { fiscalNumber: fn, fiscalName } = option.owner;
      if (!map.has(fn)) {
        map.set(fn, fiscalName);
      } else {
        const currentName = map.get(fn)!;
        if (currentName === fn && fiscalName !== fn) {
          map.set(fn, fiscalName);
        }
      }
    });
    return map;
  }, [options]);

  const filterOptions = createFilterOptions<AccountResponse>({
    stringify: (option) =>
      `${option.owner.fiscalName} ${option.owner.fiscalNumber} ${option.institution.name} ${option.account.branch} ${option.account.accountNumber}`,
  });

  return (
    <Autocomplete
      value={selectedAccount}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={options}
      filterOptions={filterOptions}
      getOptionLabel={(option: AccountResponse) =>
        `${option.institution.name} - ${option.account.branch} ${option.account.accountNumber}`
      }
      isOptionEqualToValue={(option: AccountResponse, val: AccountResponse) =>
        option.account.accountId === val.account.accountId
      }
      groupBy={(option: AccountResponse) => {
        const { fiscalNumber: fn } = option.owner;
        const bestName = fiscalNumberToName.get(fn) || fn;
        return `${bestName} - ${formatCnpj(fn)}`;
      }}
      loading={isLoading}
      renderInput={(params) => {
        let displayLabel = label;
        if (selectedAccount) {
          displayLabel = `${selectedAccount.owner.fiscalName} - ${selectedAccount.account.branch} - ${selectedAccount.account.accountNumber}`;
        }
        return (
          <AccountAutocompleteInput
            params={params}
            label={displayLabel}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
          />
        );
      }}
      renderGroup={(params) => (
        <li key={params.key}>
          <div style={{ padding: '8px 16px', fontSize: 12, fontWeight: 600, color: '#6b7280', lineHeight: 1.3, position: 'sticky', top: 0, backgroundColor: '#f5f8ff', zIndex: 1 }}>
            {params.group}
          </div>
          <ul style={{ padding: 0, margin: 0, listStyle: 'none', marginTop: 6 }}>
            {params.children}
          </ul>
        </li>
      )}
      renderOption={(props, option) => (
        <TransferOption {...props} option={option} />
      )}
      fullWidth
      ListboxProps={{
        sx: {
          "& .MuiAutocomplete-option": {
            padding: "14px 16px !important",
          },
        },
      } as any}
    />
  );
};
